import { useMemo, useState } from 'react'
import { useSuspenseInfiniteQuery } from '@tanstack/react-query'
import { createFileRoute, Link, notFound, useNavigate } from '@tanstack/react-router'
import type { ColumnDef } from '@tanstack/react-table'
import { UsersIcon } from 'lucide-react'

import { Button } from '@ems/ui/components/button'
import { SearchInput } from '@ems/ui/patterns/search-input'
import { DataTable } from '@ems/ui/patterns/data-table'
import { EmptyState } from '@ems/ui/patterns/empty-state'
import { PageHeader } from '@ems/ui/patterns/page-header'
import { StatusBadge } from '@ems/ui/patterns/status-badge'

import { NotFoundView } from '#admin/components/not-found-view.tsx'
import { sessionQuery } from '#admin/features/auth/queries.ts'
import { isPlatformStaff } from '#admin/features/platform/api.ts'
import type { UserSummary } from '#admin/features/platform/users/api.ts'
import { usersQuery } from '#admin/features/platform/users/queries.ts'
import { UsersSkeleton } from '#admin/features/platform/platform-skeletons.tsx'

/**
 * `/platform/users` — every login on the platform.
 *
 * The search term lives in the URL (`?q=`), so a link to "the users matching acme.com" can be
 * pasted into a support ticket, and Back returns to the same results.
 *
 * Staff only, and a 404 otherwise — the same reasoning as `/platform`.
 */
export const Route = createFileRoute('/_authenticated/platform/users/')({
  pendingComponent: UsersSkeleton,
  validateSearch: (search: Record<string, unknown>): { q: string } => ({
    q: typeof search['q'] === 'string' ? search['q'].slice(0, 200) : '',
  }),
  loaderDeps: ({ search }) => ({ q: search.q }),
  loader: async ({ context, deps }) => {
    const session = await context.queryClient.ensureQueryData(sessionQuery())
    if (!isPlatformStaff(session)) throw notFound()
    await context.queryClient.ensureInfiniteQueryData(usersQuery(deps.q))
  },
  // Its own, so the refusal renders in the layout's outlet with the sidebar intact. A thrown
  // not-found goes to the nearest route declaring a component for it, and that route renders it in
  // place of itself — so the layout must not be the one to catch it.
  notFoundComponent: NotFoundView,
  component: UsersPage,
})

function UsersPage() {
  const { q } = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const [draft, setDraft] = useState(q)
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } = useSuspenseInfiniteQuery(
    usersQuery(q),
  )
  const users = data.pages.flatMap((page) => page.items)

  const columns = useMemo<ColumnDef<UserSummary, never>[]>(
    () => [
      {
        id: 'who',
        header: 'User',
        meta: { primary: true },
        cell: ({ row }) => (
          <Link
            to="/platform/users/$userId"
            params={{ userId: row.original.id }}
            className="flex flex-col hover:underline"
          >
            <span className="font-medium">{row.original.email}</span>
            <span className="text-muted-foreground text-xs">{row.original.name}</span>
          </Link>
        ),
      },
      {
        id: 'status',
        header: 'Status',
        cell: ({ row }) => (
          <span className="flex flex-wrap gap-1">
            {row.original.banned ? <StatusBadge tone="danger">banned</StatusBadge> : null}
            {row.original.isStaff ? <StatusBadge tone="info">staff</StatusBadge> : null}
            {!row.original.banned && !row.original.isStaff ? (
              <StatusBadge tone="neutral">customer</StatusBadge>
            ) : null}
          </span>
        ),
      },
      {
        accessorKey: 'memberships',
        header: 'Editions',
        meta: { align: 'end' },
      },
      {
        id: 'created',
        header: 'Created',
        cell: ({ row }) => (
          <span className="text-muted-foreground">
            {new Date(row.original.createdAt).toLocaleDateString()}
          </span>
        ),
      },
    ],
    [],
  )

  return (
    <div className="flex flex-col gap-6">
      <PageHeader title="Users" description="Every login on the platform, across every edition." />

      <form
        role="search"
        className="flex gap-2"
        onSubmit={(event) => {
          event.preventDefault()
          void navigate({ search: { q: draft.trim() } })
        }}
      >
        <SearchInput
          aria-label="Search by email or name"
          placeholder="Search by email or name"
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          className="max-w-md"
        />
        <Button type="submit" variant="outline">
          Search
        </Button>
      </form>

      <DataTable
        caption={q === '' ? 'Every user' : `Users matching “${q}”`}
        columns={columns}
        data={users}
        getRowId={(user) => user.id}
        empty={
          <EmptyState
            icon={<UsersIcon />}
            title={q === '' ? 'No users yet' : 'Nobody matches'}
            description={q === '' ? undefined : 'The search looks at email addresses and names.'}
          />
        }
      />

      {hasNextPage ? (
        <Button
          variant="outline"
          className="self-center"
          disabled={isFetchingNextPage}
          onClick={() => void fetchNextPage()}
        >
          {isFetchingNextPage ? 'Loading…' : 'Load more'}
        </Button>
      ) : null}
    </div>
  )
}
